/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo, useState } from 'react';

import { branch } from 'recompose';

import logger from 'js/app/loggerSingleton';
import requestCountry from 'js/lib/requestCountry';

import { Button, Dialog, Divider, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import EnrollmentChoiceTypes from 'bundles/enroll-common/common/EnrollmentChoiceTypes';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-common/lib/enrollmentChoiceUtils';
import type { CHOICES_TYPE_WITH_SUBMIT_PROMISE } from 'bundles/enroll-common/lib/enrollmentChoiceUtils';
import type { ApiError } from 'bundles/enroll-common/utils/errorUtils';
import EnrollErrorModal from 'bundles/enroll-modal/components/common/EnrollErrorModal';
import S12nNonRecurringChoicesV2 from 'bundles/enroll-modal/components/s12n-non-recurring/S12nNonRecurringChoicesV2';
import { NON_RECURRING_CHOICES } from 'bundles/enroll-modal/components/s12n-non-recurring/utils/constants';
import {
  getCurrentPaymentPassOption,
  getSortedPaymentPassOptions,
  useNonRecurringChoiceData,
} from 'bundles/enroll-modal/components/s12n-non-recurring/utils/nonRecurringChoiceDataUtils';
import {
  useS12nProductInfo,
  withS12nProductInfo,
} from 'bundles/enroll-modal/components/s12n-non-recurring/withS12nProductInfo';
import type { PropsFromWithS12nProductInfo } from 'bundles/enroll-modal/components/s12n-non-recurring/withS12nProductInfo';
import S12nSubscriptionFooter from 'bundles/enroll-modal/components/s12n-subscriptions/S12nSubscriptionFooter';
import { useEnrollModalEventing } from 'bundles/enroll-modal/utils/eventingUtils';
import {
  getHoursPerWeekFromCourses,
  getTotalMonthsFromCourses,
} from 'bundles/enterprise-legacy-learner-home/utils/xdpUtils';
import paymentsExperiments from 'bundles/epic/clients/payments';
import type { PrepaidProduct } from 'bundles/ownable-products-common/types/ProductInfoTypes';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { SPECIALIZATION_PREPAID } from 'bundles/payments-common/common/ProductType';

import _t from 'i18n!nls/s12n-enroll';

const MODAL_TYPES = {
  ENROLL: 'ENROLL',
  ERROR: 'ERROR',
};

type PropsFromCaller = {
  s12nId: string;
  onSdp: boolean;
  courseId?: string;
  onClose?: () => void;
  showAuditCta?: boolean;
  preloadModal?: boolean;
};

type PropsToComponent = PropsFromCaller & PropsFromWithS12nProductInfo;
const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const styles = {
  valuePropsTitle: css`
    margin-bottom: 8px;
  `,
  valueProps: css`
    list-style-type: none;
    padding-left: 0;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  `,
  valueProp: css`
    width: 48%;
    margin-bottom: 12px;
    display: flex;

    ${breakpoints.down('xs')} {
      width: 100%;
    }
  `,
  auditCtaContainer: css`
    margin-top: 32px;
    gap: 12px;
    display: flex;
    flex-direction: column;

    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-300);
    }

    .rc-SubscriptionFooter {
      p {
        margin: 0 !important;
      }
    }
  `,
  checkMark: css`
    flex-shrink: 0;
    margin-right: var(--cds-spacing-100);
  `,
  actions: css`
    padding-top: var(--cds-spacing-400);
    padding-bottom: var(--cds-spacing-400);

    ${breakpoints.down('xs')} {
      padding-top: var(--cds-spacing-300);
      padding-bottom: var(--cds-spacing-300);
    }
  `,
  content: css`
    ${breakpoints.down('xs')} {
      padding-top: var(--cds-spacing-200);
    }
  `,
};

const getValueProps = (numOfCourses: number) => {
  if (requestCountry.get() !== 'IN') {
    return [
      _t('Unlimited access to all #{numOfCourses} courses', { numOfCourses }),
      _t('Certificate upon completion'),
      _t('7 day refund period'),
    ];
  }
  return [
    _t('Unlimited access to all #{numOfCourses} courses', { numOfCourses }),
    _t('EMI payment options'),
    _t('Certificate upon completion'),
    _t('7 day refund period'),
  ];
};

const _handleContinue = (
  setIsEnrolling: (isEnrolling: boolean) => void,
  setEnrollmentError: (error: ApiError | undefined) => void,
  setActiveModal: (modalType: string) => void,
  enrollmentChoice: keyof typeof EnrollmentChoiceTypes,
  s12nId: string,
  isFirstNonRecurringChoiceSelected?: boolean,
  courseId?: string,
  additionalData?: { paymentPassOption: PrepaidProduct },
  promoCode?: string
) => {
  setIsEnrolling(true);
  setEnrollmentError(undefined);

  const data = {
    s12nId,
    ...additionalData,
  };

  // productSkuId is not needed because this modal only shows prepaid, not subscription
  const options = {
    courseId,
    s12nId,
    data,
  };

  const handleSubmitPromise = choiceTypeToHandleSubmitPromise[enrollmentChoice as CHOICES_TYPE_WITH_SUBMIT_PROMISE];
  submitEnrollmentPromise({
    handleSubmitPromise,
    options,
    promoCode: isFirstNonRecurringChoiceSelected ? promoCode : undefined,
  }).catch((error: ApiError) => {
    setEnrollmentError(error);
    setActiveModal(MODAL_TYPES.ERROR);
  });
};

export const S12nNonRecurringEnrollModalV2 = ({
  productName,
  product,
  s12nName,
  numOfCourses,
  courses,
  onSdp,
  s12nId,
  courseId,
  onClose,
  promotionDetails,
  promotionEligibilities,
  showAuditCta = true,
}: PropsToComponent) => {
  const { getEnrollModalTrackingRef, trackEnrollModalClose, trackEnrollModalContinue } = useEnrollModalEventing();

  const [activeModal, setActiveModal] = useState(MODAL_TYPES.ENROLL);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrollmentError, setEnrollmentError] = useState<ApiError | undefined>(undefined);
  const [choiceType, setChoiceType] = useState(NON_RECURRING_CHOICES.PASS_TWO);
  const isScreenXsDown = useMediaQuery(breakpoints.down('xs'));

  const visibilityTrackingRef = getEnrollModalTrackingRef();

  const numOfHoursPerWeek = getHoursPerWeekFromCourses(courses);
  const numOfMonths = getTotalMonthsFromCourses(courses);

  const { prepaidOptions } = useNonRecurringChoiceData(s12nId);

  const promoCode = promotionEligibilities?.isEligible ? promotionEligibilities?.promoCodeId : undefined;

  const auditComponent = !onSdp ? (
    <div css={styles.auditCtaContainer}>
      <Divider />
      <S12nSubscriptionFooter removeStyles />
    </div>
  ) : undefined;

  const specializationInfoText = useMemo(() => {
    return onSdp
      ? _t('#{productName} is a #{numOfCourses}-course #{product}.', { productName, numOfCourses, product })
      : _t('#{productName} is part of the #{s12nName} #{product}.', { productName, s12nName, product });
  }, [productName, numOfCourses, s12nName, onSdp, product]);

  const valuePropsDiv = useMemo(() => {
    const valueProps = getValueProps(numOfCourses);
    return (
      <div>
        <Typography2
          component="div"
          variant="bodyPrimary"
          aria-hidden={true}
          css={styles.valuePropsTitle}
          id="value-props-description"
        >
          {_t('This #{product} includes:', { product })}
        </Typography2>
        <ul css={styles.valueProps} aria-labelledby="value-props-description">
          {valueProps.map((valueProp) => (
            <li data-testid="valueProp" key={valueProp} css={styles.valueProp}>
              <CheckIcon size="large" color="interactive" css={styles.checkMark} />
              <Typography2 component="span" variant="bodySecondary">
                {valueProp}
              </Typography2>
            </li>
          ))}
        </ul>
      </div>
    );
  }, [product, numOfCourses]);

  const sortedPaymentPassOptions = prepaidOptions ? getSortedPaymentPassOptions(prepaidOptions) : null;
  const paymentPassOption = sortedPaymentPassOptions
    ? getCurrentPaymentPassOption(choiceType, sortedPaymentPassOptions)
    : null;

  if (!prepaidOptions) {
    logger.error('Unable to render enroll modal content');
    return null;
  }

  const handleClose = () => {
    if (onClose) {
      trackEnrollModalClose();
      onClose();
    }
  };

  const handleContinue = (_paymentPassOption: PrepaidProduct) => {
    const enrollmentChoiceType =
      _paymentPassOption.productType === SPECIALIZATION_PREPAID
        ? EnrollmentChoiceTypes.ENROLL_THROUGH_S12N_PREPAID
        : EnrollmentChoiceTypes.PURCHASE_THROUGH_COURSERA_PLUS;
    const ownershipDays = _paymentPassOption.productProperties.ownershipDays;

    trackEnrollModalContinue(enrollmentChoiceType, { ownershipDays });

    _handleContinue(
      setIsEnrolling,
      setEnrollmentError,
      setActiveModal,
      enrollmentChoiceType,
      s12nId,
      choiceType === NON_RECURRING_CHOICES.PASS_ONE,
      courseId,
      {
        paymentPassOption: _paymentPassOption,
      },
      promoCode
    );
  };

  const { HeadingGroup, Content, Actions } = Dialog;

  switch (activeModal) {
    case MODAL_TYPES.ENROLL: {
      return (
        <Dialog
          color="darkBlue"
          aria-describedby="dialog-content"
          open
          onClose={handleClose}
          ref={visibilityTrackingRef}
          sticky="actions"
          data-testid="rc-NonRecurringEnrollModal"
        >
          <HeadingGroup supportText={specializationInfoText}>
            {onSdp
              ? _t('Enroll in this #{numOfCourses}-course #{product}', { numOfCourses, product })
              : _t('This course is part of a #{numOfCourses}-course #{product}', { numOfCourses, product })}
          </HeadingGroup>
          <Content id="dialog-content" css={styles.content}>
            <TrackedDiv
              withVisibilityTracking={true}
              trackingName="non_recurring_enroll_modal"
              trackingData={{ id: s12nId }}
            >
              {valuePropsDiv}
              <S12nNonRecurringChoicesV2
                numOfHoursPerWeek={numOfHoursPerWeek}
                numOfMonths={numOfMonths}
                choiceType={choiceType}
                setChoiceType={setChoiceType}
                isFullDiscount={!!promoCode && promotionDetails?.discountPercent === 100}
                s12nId={s12nId}
              />
            </TrackedDiv>
          </Content>
          <Actions css={styles.actions}>
            <TrackedButton
              data-testid="continueButton"
              trackingName="continue_button"
              loading={isEnrolling}
              onClick={() => paymentPassOption && handleContinue(paymentPassOption)}
              disabled={isEnrolling || !paymentPassOption}
              variant="primary"
              fullWidth={isScreenXsDown}
            >
              {_t('Continue')}
            </TrackedButton>
            {showAuditCta && auditComponent}
          </Actions>
        </Dialog>
      );
    }
    case MODAL_TYPES.ERROR:
      return (
        <EnrollErrorModal
          error={enrollmentError}
          onClose={handleClose}
          isFinancialAid={false}
          s12nId={s12nId}
          courseId={courseId}
        />
      );
    default:
      logger.error('Unable to render S12nNonRecurringEnrollModal');
      return null;
  }
};

const NonRecurringEnrollModalV2WithHook = (props: PropsFromCaller) => {
  const { courseId, preloadModal } = props;
  // We want to preload the modal if the experiment is enabled or if the preloadModal prop is true
  // the preloadModal prop is used to preload the modal for the freemium upgrade cta
  const skip = !paymentsExperiments.preview('preloadEnrollModalEnabled') && !preloadModal;
  const s12nProductInfoProps = useS12nProductInfo({ courseId, skip });

  return <S12nNonRecurringEnrollModalV2 {...props} {...(s12nProductInfoProps as PropsFromWithS12nProductInfo)} />;
};

export default branch<PropsFromCaller>(
  ({ preloadModal }) => !paymentsExperiments.preview('preloadEnrollModalEnabled') && !preloadModal,
  withS12nProductInfo<PropsFromCaller>()
)(NonRecurringEnrollModalV2WithHook);
